import { useId, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { normaliseUrl } from './run';
import './ImportField.css';

/**
 * The paste-a-link half of the add-item dialog. Sits above the file drop,
 * because on a shop page the link is the faster path and the photo well is
 * the fallback rather than the other way round.
 *
 * Pressing Import doesn't wait for anything — the item is created and the
 * panel opens on it immediately, in a skeleton state.
 */
export function ImportField({
  busy,
  onImport,
}: {
  busy: boolean;
  onImport: (url: string) => void;
}) {
  const fieldId = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit() {
    const url = normaliseUrl(value);
    if (url === undefined) {
      setError("That doesn't look like a link.");
      return;
    }

    setError(undefined);
    onImport(url);
  }

  return (
    <div className="field">
      <label className="section-label" htmlFor={fieldId}>
        From A Link
      </label>

      <div className="import-field">
        <Link2 className="import-field__glyph" size={16} aria-hidden />

        <input
          id={fieldId}
          className="input import-field__input"
          value={value}
          placeholder="Paste a shop link"
          inputMode="url"
          autoFocus
          onChange={(event) => {
            setValue(event.target.value);
            setError(undefined);
          }}
          // Enter here means import, not submit-the-whole-dialog — that would
          // try to create an item from an empty photo well.
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            submit();
          }}
        />

        <Button variant="quiet" onClick={submit} disabled={busy || value.trim() === ''}>
          Import
        </Button>
      </div>

      {error !== undefined && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
